
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import api from "../utils/api";
import { tSuccess, tError } from "../utils/toast";
import { emitLeadUpdate } from "../utils/leadSync";

/** Local calendar date YYYY-MM-DD for route.date on create. */
function localTodayDateString() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const SaveRouteModal = ({ open, onClose, route = null, selectedLeads = [], token }) => {
  const [routeName, setRouteName] = useState("");
  const [assignedRep, setAssignedRep] = useState("");
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // ✅ Fetch reps once
  useEffect(() => {
    const fetchReps = async () => {
      try {
        const res = await api.get("/users/", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setReps(res.data);
      } catch (err) {
        console.error("Error fetching reps:", err);
      }
    };
    fetchReps();
  }, [token]);

  // ✅ Save route
  const handleSaveRoute = async () => {
    const validName = routeName.trim() !== "";
    const validLeads = Array.isArray(selectedLeads) && selectedLeads.length >= 2;

    if (!validName || !validLeads) {
      tError("Please name the route and select at least two leads before saving.");
      console.warn("Validation failed →", {
        name: routeName,
        leadsCount: selectedLeads?.length || 0,
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
  name: routeName,
  date: localTodayDateString(),
  assigned_rep: assignedRep || null,
  geometry: route || null, // LeadMap doesn't include geometry yet
  route_data: {
    stops: selectedLeads.map((l, i) => ({
      id: `coord-${i + 1}`,
      lead_id: l.id,                             // ✅ Real backend ID
      first_name: l.first_name || "",            // ✅ Added
      last_name: l.last_name || "",              // ✅ Added
      name: `${l.first_name || ""} ${l.last_name || ""}`.trim(), // ✅ Keeps combined name
      address: l.address || "—",
      city: l.city || "",
      state: l.state || "",
      zip_code: l.zip_code || "",
      latitude: l.latitude,
      longitude: l.longitude,
      status: l.status || "New",
      notes: l.notes || "",
    })),
  },
};

      await api.post("/routeplans/", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      tSuccess("Route saved successfully!");

      // Emit lead updates for all selected leads so AddLeadPage.js can refresh
      // Determine which rep ID to use: assignedRep if set, otherwise current user
      const repId = assignedRep || null;
      if (selectedLeads && selectedLeads.length > 0) {
        selectedLeads.forEach(lead => {
          emitLeadUpdate({ 
            id: lead.id, 
            assigned_rep: repId 
          });
        });
      }

      navigate("/leadmap/savedroutes");
      setRouteName("");
      setAssignedRep("");
      onClose();
    } catch (err) {
      console.error("🚨 Error saving route:", err.response?.data || err.message);
      tError("Failed to save route. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Save Optimized Route</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="dense"
          label="Route Name"
          value={routeName}
          onChange={(e) => setRouteName(e.target.value)}
          required
        />
        <TextField
          fullWidth
          select
          margin="dense"
          label="Assign to Rep (optional)"
          value={assignedRep}
          onChange={(e) => setAssignedRep(e.target.value)}
        >
          <MenuItem value="">None</MenuItem>
          {reps.map((rep) => (
            <MenuItem key={rep.id} value={rep.id}>
              {rep.username || rep.email}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleSaveRoute}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Save Route"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveRouteModal;
